<template>
  <transition name="fade">
    <div v-if="canShow" class="modal-container">
      <button @click="close" class="modal-close" :style="{ top: `${closeButtonOffsetY - 10}px` }">
        <img
          :src="require('@/assets/img/coupon-with-cart/modal-close-icon.svg')"
          alt=""
        >
      </button>
      <div
        v-if="-1 < selectedTypeLabelOffsetY"
        class="selected-type-label"
        :style="{ top: `${selectedTypeLabelOffsetY - selectedTypeLabelOffsetAdjuster}px` }"
      >
        {{ selectedTypeLabel }}
      </div>
      <transition name="fade-faster" v-on:after-leave="afterLeaveFlickity">
        <component
          :is="canCarousel ? 'flickity' : 'div'"
          v-show="showFlickity"
          id="flickity"
          :class="!canCarousel && 'cantCarousel'"
          ref="flickity"
          @change="onChangeSlide"
          :options="flickityOptions"
        >
          <div
            :id="coupon.data.coupon_id || coupon.data.prize_id"
            class="carousel-cell"
            :class="!canCarousel && 'cantCarousel'"
            v-for="(coupon, i) in couponsForDisplay"
            :key="i"
          >
            <coupon-with-cart-card
              :coupon="coupon"
              :index="i"
              :display-from="mode === modalModeNormal ? screenTypeModal : screenTypeUsedModal"
              @close-modal="close"
              @card-loaded="({ top, bottom }) => cardLoaded({ top, bottom })"
              @open-external-url="openExternalUrl"
              @show-usage-dialog="({ content, isHtmlDisplay }) => showUsageDialog({ content, isHtmlDisplay })"
            />
          </div>
        </component>
      </transition>
      <div
        v-if="-1 < pageLabelOffsetY && canCarousel"
        class="page-label"
        :style="{ top: `${pageLabelOffsetY + pageLabelOffsetAdjuster}px` }"
      >
        <span>{{ pageLabel }}</span>
      </div>
      <coupon-with-cart-dialog
        key="usageDialog"
        ref="usageDialog"
        negative-button-label="閉じる"
        @negative="closeUsageDialog"
      />
      <coupon-with-cart-tutorial v-if="canCarousel" />
    </div>
  </transition>
</template>

<script>
import Flickity from 'vue-flickity-2/src/Flickity'
import 'vue-flickity-2/dist/vue-flickity-2.css'
import { mapActions, mapGetters } from 'vuex'
import CouponWithCartCard from '@/views/CouponWithCart/Card.vue'
import CouponWithCartDialog from '@/views/CouponWithCart/Dialog.vue'
import { COUPON_SELECT_TYPE, SCREEN_TYPE } from '@/store/couponWithCart'
import CouponWithCartTutorial from '@/views/CouponWithCart/Tutorial.vue'

export const MODAL_MODE = {
  normal: 0,
  used: 1
}

export default {
  name: 'CouponWithCartModal',
  components: {
    CouponWithCartTutorial,
    CouponWithCartDialog,
    'flickity': Flickity,
    CouponWithCartCard
  },
  props: {
    mode: { type: Number, default: MODAL_MODE.normal },
    closeButtonOffsetY: { type: Number, default: 0 }
  },
  data() {
    return {
      showFlickity: false,
      flickityOptions: {
        prevNextButtons: false,
        pageDots: false,
        dragThreshold: 30
      },
      currentPage: 1,
      selectedTypeLabelOffsetY: -1,
      selectedTypeLabelOffsetAdjuster: 40,
      pageLabelOffsetY: -1,
      pageLabelOffsetAdjuster: 10,
      screenTypeModal: SCREEN_TYPE.modal,
      screenTypeUsedModal: SCREEN_TYPE.usedModal,
      modalModeNormal: MODAL_MODE.normal
    }
  },
  computed: {
    ...mapGetters({
      coupons: 'CouponWithCart/couponsForModal',
      usedCoupons: 'CouponWithCart/usedCoupons',
      selectedType: 'CouponWithCart/selectedType',
      selectedCoupons: 'CouponWithCart/selectedCoupons',
      showModal: 'CouponWithCart/showModal',
      showUsedModal: 'CouponWithCart/showUsedModal'
    }),
    couponsForDisplay() {
      return this.mode === MODAL_MODE.normal ? this.coupons : this.usedCoupons
    },
    pageLabel() {
      return `${this.currentPage} / ${this.couponsForDisplay.length}`
    },
    selectedTypeLabel() {
      if (this.usedCoupons.length > 0) {
        return `ご利用中のクーポン：${this.usedCoupons.length}枚`
      }

      let labelPrefix = ''
      if (this.selectedType.key === COUPON_SELECT_TYPE.eatIn.key) {
        labelPrefix = '店舗で使う'
      }
      return `${labelPrefix}クーポンを選択`
    },
    canShow() {
      return this.mode === MODAL_MODE.normal ? this.showModal : this.showUsedModal
    },
    canCarousel() {
      const ua = navigator.userAgent
      if (ua.indexOf('Android') > 0) {
        const version = parseFloat(ua.slice(ua.indexOf('Android') + 8))
        if (version <= 4.4) {
          return false
        }
      }
      return true
    }
  },
  watch: {
    showModal(showModal) {
      this.scrollLock(showModal)
    },
    showUsedModal(showUsedModal) {
      this.scrollLock(showUsedModal)
    }
  },
  methods: {
    ...mapActions('CouponWithCart', [
        'setSelectedType',
        'clearUsedCoupons',
        'setShowModal',
        'setShowUsedModal'
      ]
    ),
    ...mapActions('Loading', ['actionToggleLoading']),
    show(coupon = null) {
      this.showFlickity = true
      if (this.mode === MODAL_MODE.normal) {
        this.setShowModal(true)
      } else {
        this.setShowUsedModal(true)
      }

      this.$nextTick(() => {
        const couponIndex = coupon ? this.couponsForDisplay.findIndex((c) => {
          return c.data.coupon_id === coupon.data.coupon_id && c.data.prize_id === coupon.data.prize_id
        }) : 0
        const selectIndex = couponIndex > -1 ? couponIndex : 0

        if (this.canCarousel) {
          this.$refs.flickity.flickity.select(selectIndex, false, true)
        } else {
          const target = coupon ? document.getElementById(coupon.data.coupon_id || coupon.data.prize_id) : null
          target.scrollIntoView()
        }

        this.currentPage = selectIndex + 1
      })
    },
    close() {
      this.showFlickity = false
    },
    afterLeaveFlickity() {
      this.selectedTypeLabelOffsetY = -1
      this.pageLabelOffsetY = -1
      this.currentPage = 1
      if (this.selectedCoupons.length < 1) {
        this.setSelectedType(COUPON_SELECT_TYPE.notYet)
      }
      if (this.mode === MODAL_MODE.normal) {
        this.setShowModal(false)
      } else {
        this.setShowUsedModal(false)
        this.clearUsedCoupons()
      }
    },
    onChangeSlide(index) {
      this.currentPage = index + 1
    },
    cardLoaded({ top, bottom }) {
      this.selectedTypeLabelOffsetY = (this.selectedTypeLabelOffsetY === -1 || top < this.selectedTypeLabelOffsetY) ?
        top :
        this.selectedTypeLabelOffsetY

      this.pageLabelOffsetY = (this.pageLabelOffsetY === -1 || this.pageLabelOffsetY < bottom) ?
        bottom :
        this.pageLabelOffsetY
    },
    openExternalUrl(url) {
      this.$emit('open-external-url', url)
    },
    showUsageDialog({ content, isHtmlDisplay }) {
      this.$refs.usageDialog.show({
        title: '利用条件',
        content,
        isHtmlDisplay
      })
    },
    closeUsageDialog() {
      this.$refs.usageDialog.close()
    },
    scrollLock(needsLock) {
      document.body.className = needsLock ? 'scroll-lock' : ''
    }
  }
}

</script>

<style lang="scss" scoped>
.fade-faster-enter-active, ::v-deep .fade-faster-leave-active {
  transition: opacity 0.1s;
}

.modal-container {
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.85);
  overflow: hidden;
  z-index: 2;

  .modal-close {
    position: absolute;
    top: 0;
    right: 0;
    width: 44px;
    height: 44px;
    z-index: 2;
  }

  .selected-type-label, .page-label {
    position: absolute;
    left: 0;
    text-align: center;
    width: 100%;
  }

  .selected-type-label {
    color: #ffffff;
    font-weight: bold;
  }

  .page-label {
    color: #ffffff;

    span {
      background: #bbbbbb;
      padding: 4px 12px;
      font-size: 10px;
      border-radius: 1em;
    }
  }

  #flickity {
    height: 100%;

    &.cantCarousel {
      display: flex;
      overflow: scroll;
    }

    .carousel-cell {
      &.cantCarousel {
        flex-shrink: 0;
      }

      width: 90%;
      height: 100%;
      padding: 5px;
      display: flex;
      justify-content: center;
      align-items: center;
      // クーポンカード内で inner scroll させる場合は下記を有効化
      // TODO: ただし Android で flickity のスワイプができなくなる場合があるため要修正
      // touch-action: pan-y;
    }
  }
}

</style>
